import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { PropTypes } from 'prop-types';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { useFetchRegions } from 'api/regions/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { DASHBOARD, REGION } from 'constants/routes';
import { useTranslation } from 'react-i18next';
import { RamblaActionIcon } from 'components';
import { TOUR_TARGETS } from 'components/UserTour/constants/tourTargets';
import EntityForm from 'containers/EntityForm/entityForm';
import { FiSettings } from 'react-icons/fi';
import SwitcherDropdown from './SwitcherDropdown';
import * as Ui from './RegionSwitcher.styles';

const RegionItem = styled.li`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background-color: #444;
  }
`;

const RegionActionItem = styled.li`
  background: #be1622;
  &:hover {
    background-color: #a2131d;
  }
`;

const RegionActionButton = styled.button`
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  text-align: center;
  border-width: 0;
  background: transparent;
  text-transform: uppercase;
`;

const RegionItemName = styled.span`
  font-size: 14px;
  color: ${({ isActive }) => (isActive ? '#fff' : '#eee')};
  font-weight: ${({ isActive }) => (isActive ? 'bold' : 'normal')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActiveRegionLabel = styled.span`
  margin: 0;
  font-size: 12px;
  color: #ccc;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ActiveRegionName = styled.span`
  margin: 0;
  font-size: 14px;
  color: #fff;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActiveRegionInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RegionIconBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: #444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  color: #fff;
  flex-shrink: 0;

  ${({ $compact }) =>
    $compact &&
    css`
      width: 30px;
      height: 30px;
    `}

  ${({ isActive }) =>
    isActive &&
    css`
      border: 1px solid #be1622;
    `}
`;

const RegionManagerButton = styled.button`
  width: ${({ $compact }) => ($compact ? '24px' : '28px')};
  height: ${({ $compact }) => ($compact ? '24px' : '28px')};
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #ccc;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover,
  &:focus {
    background-color: #444;
    color: #fff;
    outline: none;
  }
`;

const RegionSwitcher = ({ compact, regionSwitcherOpened, setRegionSwitcherOpened }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isCreateRegionModalOpen, setIsCreateRegionModalOpen] = useState(false);
  const selectedRegion = useSelectedProfileStore((state) => state.selectedRegionUid);
  const setSelectedRegion = useSelectedProfileStore((state) => state.setSelectedRegion);

  const { data: regions = [] } = useFetchRegions();

  const activeRegion = regions.find((region) => region.uid === selectedRegion) || regions[0];

  useEffect(() => {
    if (activeRegion?.uid && activeRegion.uid !== selectedRegion) {
      setSelectedRegion(activeRegion.uid);
    }
  }, [activeRegion, selectedRegion, setSelectedRegion]);

  const handleRegionClick = (region, e) => {
    e.stopPropagation();
    setSelectedRegion(region.uid);
    if (regionSwitcherOpened) {
      setRegionSwitcherOpened(false);
    }
    queryClient.resetQueries();
    navigate(DASHBOARD);
  };

  const handleRegionManagerClick = (e) => {
    e.stopPropagation();
    if (regionSwitcherOpened) {
      setRegionSwitcherOpened(false);
    }
    navigate(REGION);
  };

  const regionManagerLabel = t('menu:regionsManager');

  return (
    <SwitcherDropdown
      compact={compact}
      isOpen={regionSwitcherOpened}
      hasManyItems
      onToggle={() => setRegionSwitcherOpened((prev) => !prev)}
      icon={
        <RegionIconBadge $compact={compact}>
          {activeRegion?.name?.slice(0, 2).toUpperCase() ?? '+'}
        </RegionIconBadge>
      }
      content={
        !compact && (
          <ActiveRegionInfo>
            <ActiveRegionLabel>{t('app:region')}</ActiveRegionLabel>
            <ActiveRegionName>
              {activeRegion?.name
                ? activeRegion.name.toUpperCase()
                : t('pages:regions.createRegion')}
            </ActiveRegionName>
          </ActiveRegionInfo>
        )
      }
      action={
        <RegionManagerButton
          type="button"
          $compact={compact}
          className={TOUR_TARGETS.REGIONS_MANAGER}
          aria-label={regionManagerLabel}
          title={regionManagerLabel}
          onClick={handleRegionManagerClick}
        >
          <FiSettings size={compact ? 15 : 17} aria-hidden="true" />
        </RegionManagerButton>
      }
    >
      <RegionActionItem>
        <RegionActionButton
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setRegionSwitcherOpened(false);
            setIsCreateRegionModalOpen(true);
          }}
        >
          <RamblaActionIcon name="addModule" />
          {t('pages:regions.createRegion')}
        </RegionActionButton>
      </RegionActionItem>
      {regions.map((region) => (
        <RegionItem key={region.uid} onClick={(e) => handleRegionClick(region, e)}>
          <RegionIconBadge isActive={region.uid === selectedRegion} style={{ marginRight: 10 }}>
            {region.name.slice(0, 2).toUpperCase()}
          </RegionIconBadge>
          <RegionItemName isActive={region.uid === selectedRegion}>
            {region.name.toUpperCase()}
          </RegionItemName>
        </RegionItem>
      ))}
      <Ui.Modal
        open={isCreateRegionModalOpen}
        onClose={() => setIsCreateRegionModalOpen(false)}
        className="modal-lg"
        header={{
          title: t('pages:regions.createRegion'),
          subTitle: t('pages:regions.regions'),
        }}
      >
        <EntityForm entity="addRegion" type="create" />
      </Ui.Modal>
    </SwitcherDropdown>
  );
};

RegionSwitcher.propTypes = {
  compact: PropTypes.bool,
  regionSwitcherOpened: PropTypes.bool,
  setRegionSwitcherOpened: PropTypes.func,
};

export default RegionSwitcher;
